// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useMemo } from 'react';
import * as yup from 'yup';
import type { StepComponentProps } from '../../types/wizard';
import { toIsoDate } from '../../utils/date';
import { PhoneFlagDropdown, DEFAULT_PHONE_COUNTRY } from '../shared/PhoneFlagDropdown';
import { getCountryCallingCode } from 'react-phone-number-input';
import {
  getCountryConfig,
  validatePostalCode,
  SUPPORTED_COUNTRIES,
} from '../../config/addressConfig';

function buildPolicyholderSchema(countryCode: string) {
  const config = getCountryConfig(countryCode);

  return yup.object({
    policyholderName: yup.string().trim().required('Name is required'),
    policyholderDateOfBirth: yup.string()
      .required('Date of birth is required')
      .test('is-past', 'Date of birth must be in the past', (value) => {
        if (!value) return false;
        return new Date(value) < new Date();
      })
      .test('min-age', 'Policy holder must be at least 18 years old', (value) => {
        if (!value) return false;
        const birth = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
        return age >= 18;
      }),
    policyholderEmail: yup.string().trim().required('Email is required').email('Invalid email format'),
    policyholderPhone: yup.string()
      .required('Phone is required')
      .transform((value: string) => value?.replace(/\s/g, '') || '')
      .test('phone-min-digits', 'Phone is required', (value) => {
        if (!value || value === '+') return false;
        return value.replace(/^\+/, '').length >= 7;
      })
      .matches(/^\+?[1-9]\d{1,14}$/, 'Invalid phone format (e.g., +353851234567)'),
    policyholderCountryCode: yup.string().required('Country is required'),
    policyholderAddressLine1: yup.string().trim().required('Address line 1 is required'),
    policyholderAddressLine2: yup.string().trim().required('Address line 2 is required'),
    policyholderCity: yup.string().trim().required(`${config.townCityLabel} is required`),
    policyholderPostalCode: yup.string()
      .optional()
      .test('postal-format', `Invalid ${config.postalCodeLabel} format. Expected: ${config.postalCodeFormat}`, (value) => {
        if (!value || value.trim() === '') return true;
        return validatePostalCode(countryCode, value);
      }),
  });
}

export function StepPolicyholderDetails({
  data,
  onFieldChange,
  fieldErrors,
  onClearFieldError,
}: StepComponentProps) {
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});

  const FIELD_DEFAULT_COUNTRY = 'GBR';
  const selectedCountryCode = (data.policyholderCountryCode as string) || '';
  // Labels/validation use GBR defaults until user picks a country
  const effectiveCountryCode = selectedCountryCode || FIELD_DEFAULT_COUNTRY;
  const countryConfig = useMemo(() => getCountryConfig(effectiveCountryCode), [effectiveCountryCode]);

  const policyholderSchema = useMemo(() => buildPolicyholderSchema(effectiveCountryCode), [effectiveCountryCode]);

  // Initialize phone with default dial code if empty
  if (!data.policyholderPhone) {
    const defaultDial = getCountryCallingCode(DEFAULT_PHONE_COUNTRY);
    onFieldChange('policyholderPhone', `+${defaultDial} `);
  }

  const validateField = useCallback((fieldName: string, fieldData: Record<string, unknown>): string | undefined => {
    try {
      policyholderSchema.validateSyncAt(fieldName, fieldData);
      return undefined;
    } catch (err) {
      if (err instanceof yup.ValidationError) return err.message;
      return undefined;
    }
  }, [policyholderSchema]);

  const handleBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, data as Record<string, unknown>);
    setInlineErrors((prev) => {
      if (error) return { ...prev, [field]: error };
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, [data, validateField]);

  const handleCountryChange = useCallback((code: string) => {
    const country = SUPPORTED_COUNTRIES.find(c => c.value === code);
    onFieldChange('policyholderCountryCode', code);
    onFieldChange('policyholderCountry', country?.text || code);
    onClearFieldError('policyholderCountryCode');
    // Clear postal code errors since validation pattern changes per country
    onClearFieldError('policyholderPostalCode');
    setInlineErrors((prev) => {
      const next = { ...prev };
      delete next.policyholderPostalCode;
      return next;
    });
  }, [onFieldChange, onClearFieldError]);

  const getError = (field: string): string | undefined => {
    return (touched[field] ? inlineErrors[field] : undefined) || fieldErrors[field];
  };

  return (
    <div className="flex flex-col gap-lg">
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Policy holder details
      </div>

      {/* Row 1: Name* | Date of Birth* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Full name of policy holder<span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="text"
            autoComplete="name"
            value={(data.policyholderName as string) || ''}
            onChange={(e) => {
              onFieldChange('policyholderName', e.target.value);
              onClearFieldError('policyholderName');
            }}
            onBlur={() => handleBlur('policyholderName')}
            className={`w-full h-[48px] px-md rounded-lg border ${getError('policyholderName') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`}
          />
          {getError('policyholderName') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('policyholderName')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Date of birth <span className="text-[#E7000B]">*</span>
          </div>
          <div className="relative">
            <input
              type="date"
              value={(data.policyholderDateOfBirth as string) || ''}
              onBlur={() => {
                handleBlur('policyholderDateOfBirth');
              }}
              onChange={(e) => {
                onFieldChange('policyholderDateOfBirth', toIsoDate(e.target.value));
                onClearFieldError('policyholderDateOfBirth');
              }}
              className={`w-full h-[48px] px-md pr-2xl rounded-lg border ${getError('policyholderDateOfBirth') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data.policyholderDateOfBirth as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
            />
            <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask_dob" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                <path d="M16 0H0V16H16V0Z" fill="white"/>
              </mask>
              <g mask="url(#mask_dob)">
                <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
                <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
                <path d="M5.66667 9.66686C5.58 9.66686 5.49333 9.64686 5.41333 9.61353C5.32667 9.5802 5.26 9.53353 5.19333 9.47353C5.07333 9.34686 5 9.1802 5 9.0002C5 8.91353 5.02 8.82686 5.05333 8.74686C5.08667 8.66686 5.13333 8.59353 5.19333 8.52686C5.26 8.46686 5.32667 8.4202 5.41333 8.38686C5.65333 8.28686 5.95333 8.3402 6.14 8.52686C6.26 8.65353 6.33333 8.82686 6.33333 9.0002C6.33333 9.0402 6.32667 9.08686 6.32 9.13353C6.31333 9.17353 6.3 9.21353 6.28 9.25353C6.26667 9.29353 6.24667 9.33353 6.22 9.37353C6.2 9.40686 6.16667 9.4402 6.14 9.47353C6.01333 9.59353 5.84 9.66686 5.66667 9.66686Z" fill="#141414"/>
                <path d="M8 9.6672C7.91333 9.6672 7.82667 9.64727 7.74667 9.61393C7.66 9.5806 7.59333 9.53387 7.52667 9.47387C7.40667 9.3472 7.33333 9.18053 7.33333 9.00053C7.33333 8.91387 7.35333 8.8272 7.38667 8.7472C7.42 8.6672 7.46667 8.59393 7.52667 8.52727C7.59333 8.46727 7.66 8.42053 7.74667 8.3872C7.98667 8.28053 8.28667 8.3406 8.47333 8.52727C8.59333 8.65393 8.66667 8.8272 8.66667 9.00053C8.66667 9.04053 8.66 9.08727 8.65333 9.13393C8.64667 9.17393 8.63333 9.21393 8.61333 9.25393C8.6 9.29393 8.58 9.33387 8.55333 9.37387C8.53333 9.4072 8.5 9.44053 8.47333 9.47387C8.34667 9.59387 8.17333 9.6672 8 9.6672Z" fill="#141414"/>
                <path d="M10.3332 9.6672C10.2465 9.6672 10.1598 9.64727 10.0798 9.61393C9.99317 9.5806 9.9265 9.53387 9.85984 9.47387C9.83317 9.44053 9.8065 9.4072 9.77984 9.37387C9.75317 9.33387 9.73317 9.29393 9.71984 9.25393C9.69984 9.21393 9.6865 9.17393 9.67984 9.13393C9.67317 9.08727 9.6665 9.04053 9.6665 9.00053C9.6665 8.8272 9.73984 8.65393 9.85984 8.52727C9.9265 8.46727 9.99317 8.42053 10.0798 8.3872C10.3265 8.28053 10.6198 8.3406 10.8065 8.52727C10.9265 8.65393 10.9998 8.8272 10.9998 9.00053C10.9998 9.04053 10.9932 9.08727 10.9865 9.13393C10.9798 9.17393 10.9665 9.21393 10.9465 9.25393C10.9332 9.29393 10.9132 9.33387 10.8865 9.37387C10.8665 9.4072 10.8332 9.44053 10.8065 9.47387C10.6798 9.59387 10.5065 9.6672 10.3332 9.6672Z" fill="#141414"/>
                <path d="M5.66667 11.9995C5.58 11.9995 5.49333 11.9795 5.41333 11.9461C5.33333 11.9128 5.26 11.8661 5.19333 11.8061C5.07333 11.6795 5 11.5061 5 11.3328C5 11.2461 5.02 11.1595 5.05333 11.0795C5.08667 10.9928 5.13333 10.9195 5.19333 10.8595C5.44 10.6128 5.89333 10.6128 6.14 10.8595C6.26 10.9861 6.33333 11.1595 6.33333 11.3328C6.33333 11.5061 6.26 11.6795 6.14 11.8061C6.01333 11.9261 5.84 11.9995 5.66667 11.9995Z" fill="#141414"/>
                <path d="M8 11.9995C7.82667 11.9995 7.65333 11.9261 7.52667 11.8061C7.40667 11.6795 7.33333 11.5061 7.33333 11.3328C7.33333 11.2461 7.35333 11.1595 7.38667 11.0795C7.42 10.9928 7.46667 10.9195 7.52667 10.8595C7.77333 10.6128 8.22667 10.6128 8.47333 10.8595C8.53333 10.9195 8.58 10.9928 8.61333 11.0795C8.64667 11.1595 8.66667 11.2461 8.66667 11.3328C8.66667 11.5061 8.59333 11.6795 8.47333 11.8061C8.34667 11.9261 8.17333 11.9995 8 11.9995Z" fill="#141414"/>
                <path d="M10.3332 12.0006C10.1598 12.0006 9.9865 11.9273 9.85984 11.8073C9.79984 11.7473 9.75317 11.6739 9.71984 11.5873C9.6865 11.5073 9.6665 11.4206 9.6665 11.3339C9.6665 11.2473 9.6865 11.1606 9.71984 11.0806C9.75317 10.9939 9.79984 10.9206 9.85984 10.8606C10.0132 10.7073 10.2465 10.6339 10.4598 10.6806C10.5065 10.6873 10.5465 10.7006 10.5865 10.7206C10.6265 10.7339 10.6665 10.7539 10.7065 10.7806C10.7398 10.8006 10.7732 10.8339 10.8065 10.8606C10.9265 10.9873 10.9998 11.1606 10.9998 11.3339C10.9998 11.5073 10.9265 11.6806 10.8065 11.8073C10.6798 11.9273 10.5065 12.0006 10.3332 12.0006Z" fill="#141414"/>
                <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
                <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
              </g>
            </svg>
          </div>
          {getError('policyholderDateOfBirth') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('policyholderDateOfBirth')}</div>
          )}
        </div>
      </div>

      {/* Row 2: Email* | Phone* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Email of policy holder<span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="email"
            autoComplete="email"
            value={(data.policyholderEmail as string) || ''}
            onChange={(e) => {
              onFieldChange('policyholderEmail', e.target.value);
              onClearFieldError('policyholderEmail');
            }}
            onBlur={() => handleBlur('policyholderEmail')}
            className={`w-full h-[48px] px-md rounded-lg border ${getError('policyholderEmail') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`}
          />
          {getError('policyholderEmail') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('policyholderEmail')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Phone number <span className="text-[#E7000B]">*</span>
          </div>
          <div className="flex gap-sm">
            <PhoneFlagDropdown
              value={phoneCountryCode}
              onSelect={(alpha2, dialCode) => {
                setPhoneCountryCode(alpha2);
                onFieldChange('policyholderPhone', `+${dialCode} `);
                onClearFieldError('policyholderPhone');
              }}
            />
            <input
              type="tel"
              placeholder="Phone of policy holder"
              autoComplete="tel"
              value={(data.policyholderPhone as string) || ''}
              onChange={(e) => {
                onFieldChange('policyholderPhone', e.target.value);
                onClearFieldError('policyholderPhone');
              }}
              onBlur={() => handleBlur('policyholderPhone')}
              className={`flex-1 h-[48px] px-md rounded-lg border ${getError('policyholderPhone') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`}
            />
          </div>
          {getError('policyholderPhone') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('policyholderPhone')}</div>
          )}
        </div>
      </div>

      {/* Row 3: Country* | Address line 1* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Country of residential address <span className="text-[#E7000B]">*</span>
          </div>
          <div className="relative">
            <select
              value={selectedCountryCode}
              onChange={(e) => handleCountryChange(e.target.value)}
              className={`w-full h-[48px] px-md pr-xxl rounded-lg border ${getError('policyholderCountryCode') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${selectedCountryCode ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`}
            >
              <option value="" disabled className="text-[#B6B6B6]">Choose a country</option>
              {SUPPORTED_COUNTRIES.map((c) => (
                <option key={c.value} value={c.value} className="text-[#333]">{c.text}</option>
              ))}
            </select>
            <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          {getError('policyholderCountryCode') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('policyholderCountryCode')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Address line 1 <span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="text"
            autoComplete="address-line1"
            value={(data.policyholderAddressLine1 as string) || ''}
            onChange={(e) => {
              onFieldChange('policyholderAddressLine1', e.target.value);
              onClearFieldError('policyholderAddressLine1');
            }}
            onBlur={() => handleBlur('policyholderAddressLine1')}
            className={`w-full h-[48px] px-md rounded-lg border ${getError('policyholderAddressLine1') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`}
          />
          {getError('policyholderAddressLine1') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('policyholderAddressLine1')}</div>
          )}
        </div>
      </div>

      {/* Row 4: Address line 2* | Address line 3 */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Address line 2 <span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="text"
            autoComplete="address-line2"
            value={(data.policyholderAddressLine2 as string) || ''}
            onChange={(e) => {
              onFieldChange('policyholderAddressLine2', e.target.value);
              onClearFieldError('policyholderAddressLine2');
            }}
            onBlur={() => handleBlur('policyholderAddressLine2')}
            className={`w-full h-[48px] px-md rounded-lg border ${getError('policyholderAddressLine2') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`}
          />
          {getError('policyholderAddressLine2') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('policyholderAddressLine2')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Address line 3
          </div>
          <input
            type="text"
            autoComplete="address-line3"
            value={(data.policyholderAddressLine3 as string) || ''}
            onChange={(e) => {
              onFieldChange('policyholderAddressLine3', e.target.value);
            }}
            className="w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal"
          />
        </div>
      </div>

      {/* Row 5: Town/City* | Eircode/Postcode */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            {countryConfig.townCityLabel} <span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="text"
            autoComplete="address-level2"
            value={(data.policyholderCity as string) || ''}
            onChange={(e) => {
              onFieldChange('policyholderCity', e.target.value);
              onClearFieldError('policyholderCity');
            }}
            onBlur={() => handleBlur('policyholderCity')}
            className={`w-full h-[48px] px-md rounded-lg border ${getError('policyholderCity') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`}
          />
          {getError('policyholderCity') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('policyholderCity')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            {countryConfig.postalCodeLabel}
          </div>
          <input
            type="text"
            autoComplete="postal-code"
            value={(data.policyholderPostalCode as string) || ''}
            onChange={(e) => {
              onFieldChange('policyholderPostalCode', e.target.value);
              onClearFieldError('policyholderPostalCode');
            }}
            onBlur={() => handleBlur('policyholderPostalCode')}
            className={`w-full h-[48px] px-md rounded-lg border ${getError('policyholderPostalCode') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`}
          />
          {getError('policyholderPostalCode') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('policyholderPostalCode')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
